import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import DeleteIcon from '@mui/icons-material/Delete';
import CoreHttpHandler from 'http/services/CoreHttpHandler';

const VALID_EXTENSIONS = ['.mp3', '.mpeg', '.mpga', '.mp2'];
const VALID_MIME_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/x-mpeg', 'audio/mpeg3', 'audio/x-mpeg-3'];
const SIZE_LIMIT_BYTES = 20000000;

const useStyles = makeStyles(theme => ({
	uploadSection: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(3),
		border: `2px dashed ${theme.palette.divider}`,
		borderRadius: 12,
		textAlign: 'center',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)'
		}
	},
	dragActive: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(25, 118, 210, 0.08)',
		transform: 'scale(1.01)'
	},
	uploadIcon: {
		fontSize: 42,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1)
	},
	uploadText: {
		color: theme.palette.text.secondary,
		fontSize: 14
	},
	uploadHint: {
		color: theme.palette.text.disabled,
		fontSize: 12,
		marginTop: theme.spacing(0.5)
	},
	filePreview: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(25, 118, 210, 0.06)',
		borderRadius: 10,
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`
	},
	previewHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	checkIcon: {
		fontSize: 20,
		color: theme.palette.success.main
	},
	fileName: {
		flex: 1,
		fontSize: 13,
		fontWeight: 600,
		color: theme.palette.text.primary,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	audioPlayer: {
		width: '100%',
		marginTop: theme.spacing(1),
		height: 36
	},
	iconButton: {
		padding: 6,
		color: theme.palette.text.secondary
	},
	removeButton: {
		padding: 6,
		color: theme.palette.text.secondary,
		'&:hover': {
			color: theme.palette.error.main
		}
	},
	errorText: {
		color: theme.palette.error.main,
		fontSize: 12,
		marginTop: theme.spacing(0.5)
	}
}));

// Drag & drop audio uploader for the call chatbot. Exposes a `getResult()`
// imperative handle compatible with the chatbot dialog:
//   returns [error, { audio: [{ URL, caption }] }]
const CallAudioUploader = forwardRef((props, ref) => {
	const { required = false, audio: initialAudio = [], label } = props;
	const classes = useStyles();
	const inputRef = useRef();

	const initial = initialAudio && initialAudio.length && initialAudio[0] ? initialAudio[0] : null;
	const [audioItem, setAudioItem] = useState(initial); // { URL, caption, name?, size? }
	const [dragActive, setDragActive] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(null);
	const [copied, setCopied] = useState(false);

	useImperativeHandle(ref, () => ({
		getResult: () => {
			if (audioItem && audioItem.URL) {
				return [false, { audio: [audioItem] }];
			}
			if (required) {
				setError('Please upload an audio file.');
				return [true, { audio: [] }];
			}
			return [false, { audio: [] }];
		}
	}));

	const validateExtension = name => {
		const lower = (name || '').toLowerCase();
		return VALID_EXTENSIONS.some(ext => lower.endsWith(ext));
	};

	const validateMimeType = type => {
		const lower = (type || '').toLowerCase();
		return VALID_MIME_TYPES.includes(lower) || lower.includes('mpeg') || lower.includes('mp3');
	};

	const processFile = file => {
		if (!file) return;
		setError(null);

		// Accept the file if EITHER the extension or the reported MIME type is a
		// valid MPEG audio. Browsers are inconsistent about which they report.
		if (!validateExtension(file.name) && !validateMimeType(file.type)) {
			setError('Only MPEG audio files are allowed.');
			return;
		}
		if (file.size >= SIZE_LIMIT_BYTES) {
			setError('File size limit exceeded. Please select a file less than 20 MB.');
			return;
		}

		const formData = new FormData();
		const safeName = file.name.replace(/\s/g, '');
		formData.append('file', file, `${new Date().getTime()}_${safeName}`);

		setUploading(true);
		CoreHttpHandler.request(
			'content',
			'upload',
			{ params: formData },
			response => {
				setUploading(false);
				setAudioItem({
					URL: response.data.data.link,
					caption: '',
					name: file.name,
					size: file.size
				});
			},
			() => {
				setUploading(false);
				setError('Upload failed. Please try again.');
			}
		);
	};

	const handleDrag = e => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			const rect = e.currentTarget.getBoundingClientRect();
			if (
				e.clientX < rect.left ||
				e.clientX > rect.right ||
				e.clientY < rect.top ||
				e.clientY > rect.bottom
			) {
				setDragActive(false);
			}
		}
	};

	const handleDrop = e => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			processFile(e.dataTransfer.files[0]);
		}
	};

	const handleFileChange = e => {
		if (e.target.files && e.target.files[0]) {
			processFile(e.target.files[0]);
		}
		e.target.value = '';
	};

	const handleRemove = () => {
		setAudioItem(null);
		setError(null);
	};

	// Only one call-chatbot audio preview should play at a time. When this
	// player starts, pause every other call-chatbot audio element on the page.
	const handlePlay = e => {
		const current = e.currentTarget;
		document.querySelectorAll('audio[data-call-audio="true"]').forEach(el => {
			if (el !== current && !el.paused) el.pause();
		});
	};

	const handleCopy = () => {
		if (!audioItem || !audioItem.URL) return;
		navigator.clipboard
			.writeText(audioItem.URL)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			})
			.catch(() => {});
	};

	const fileLabel = audioItem ? audioItem.name || audioItem.URL.split('/').pop() : '';

	return (
		<div>
			<input
				ref={inputRef}
				type="file"
				accept="audio/mpeg,audio/mp3,.mp3,.mpeg"
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>

			{!audioItem ? (
				<div
					className={`${classes.uploadSection} ${dragActive ? classes.dragActive : ''}`}
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					onClick={() => !uploading && inputRef.current && inputRef.current.click()}
				>
					{uploading ? (
						<div className="flex flex-col items-center">
							<CircularProgress size={32} />
							<Typography className={classes.uploadText} style={{ marginTop: 8 }}>
								Uploading...
							</Typography>
						</div>
					) : (
						<>
							<Icon className={classes.uploadIcon}>graphic_eq</Icon>
							<Typography className={classes.uploadText}>
								{dragActive ? 'Drop your audio here' : label || 'Click to upload audio'}
							</Typography>
							<Typography className={classes.uploadHint}>
								or drag &amp; drop • MP3 / MPEG • Max 20MB
							</Typography>
						</>
					)}
				</div>
			) : (
				<div className={classes.filePreview}>
					<div className={classes.previewHeader}>
						<Icon className={classes.checkIcon}>check_circle</Icon>
						<Typography className={classes.fileName} title={fileLabel}>
							{fileLabel}
						</Typography>
						<Tooltip
							title={copied ? 'Copied!' : audioItem.URL}
							arrow
							PopperProps={{ style: { zIndex: 13000 } }}
						>
							<IconButton className={classes.iconButton} size="small" onClick={handleCopy}>
								{copied ? (
									<DoneIcon style={{ fontSize: 18 }} />
								) : (
									<ContentCopyIcon style={{ fontSize: 18 }} />
								)}
							</IconButton>
						</Tooltip>
						<Tooltip title="Remove" arrow PopperProps={{ style: { zIndex: 13000 } }}>
							<DeleteIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={handleRemove} />
						</Tooltip>
					</div>
					<audio
						className={classes.audioPlayer}
						controls
						src={audioItem.URL}
						data-call-audio="true"
						onPlay={handlePlay}
					>
						Your browser does not support the audio element.
					</audio>
				</div>
			)}

			{error ? <div className={classes.errorText}>{error}</div> : null}
		</div>
	);
});

export default CallAudioUploader;
